import { useState, useCallback, useMemo, useRef } from 'react'
import { useAuth } from '@/modules/auth'
import {
  RangerApi,
  RangerMessage,
  RangerChatRequest,
  RangerChatResponse,
  RangerFunctionCallResult,
} from '@/shared/api/ranger'
import { ApiError } from '@/shared/api/types'

/** A message as displayed in the thread — carries UI-only fields not sent over the wire. */
export interface RangerChatMessage extends RangerMessage {
  id: string
  functionCalls?: RangerFunctionCallResult[]
}

export interface UseRangerReturn {
  messages: RangerChatMessage[]
  sendMessage: (message: string) => Promise<void>
  loading: boolean
  error: string | null
  clearError: () => void
  clearMessages: () => void
  sessionId: string
}

/**
 * Hook for interacting with Ranger AI Assistant.
 * Generates a stable sessionId per hook instance (one per conversation / trip tab).
 * @param tripId Optional trip ID for context-aware responses
 */
export function useRanger(tripId?: string): UseRangerReturn {
  const { getAccessToken } = useAuth()
  const [messages, setMessages] = useState<RangerChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // One stable UUID per conversation — survives re-renders, resets on unmount/remount
  const sessionIdRef = useRef<string>(crypto.randomUUID())

  const api = useMemo(() => new RangerApi(getAccessToken), [getAccessToken])

  const sendMessage = useCallback(
    async (message: string) => {
      if (!message.trim() || loading) return

      setLoading(true)
      setError(null)

      // Capture current messages before adding the new one
      let currentMessages: RangerChatMessage[] = []
      setMessages(prev => {
        currentMessages = prev
        // Add user message to conversation immediately (optimistic update)
        const userMessage: RangerChatMessage = {
          id: crypto.randomUUID(),
          role: 'user',
          content: message
        }
        return [...prev, userMessage]
      })

      try {
        // Build request with conversation history (excluding the current message, and
        // stripped down to the wire shape — id/functionCalls are display-only)
        const history: RangerMessage[] = currentMessages.map(({ role, content }) => ({ role, content }))
        const request: RangerChatRequest = {
          message,
          tripId,
          sessionId: sessionIdRef.current,
          conversationHistory: history.length > 0 ? history : undefined
        }

        const response: RangerChatResponse = await api.chat(request)

        // Add assistant response to conversation
        const assistantMessage: RangerChatMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response.message,
          functionCalls: response.metadata?.functionCalls
        }
        setMessages(prev => [...prev, assistantMessage])
      } catch (err) {
        // Remove user message on error
        setMessages(prev => prev.slice(0, -1))

        // Handle different error types. Never show raw backend/AI provider messages (e.g. Bedrock) to users.
        const defaultFailureMessage = 'Something went wrong with Ranger. Please try again in a few moments.'
        if (err instanceof ApiError) {
          if (err.status === 403) {
            setError('Ranger AI Assistant requires a Pro subscription')
          } else if (err.status === 429) {
            setError('Ranger is at capacity. Please wait a moment and try again.')
          } else {
            setError(defaultFailureMessage)
          }
        } else {
          setError(defaultFailureMessage)
        }
        throw err
      } finally {
        setLoading(false)
      }
    },
    [api, tripId, loading]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
  }, [])

  return {
    messages,
    sendMessage,
    loading,
    error,
    clearError,
    clearMessages,
    sessionId: sessionIdRef.current,
  }
}

